import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, BarChart03, BrainCircuit, ChevronRight, CircleDollarSign, Database,
  Info, Layers3, Plus, RefreshCcw, Search, ShieldCheck, Sparkles, Target,
  TrendingUp, UserCheck,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { Chip, SourceTag, StageBadge } from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { initials, money } from "@/lib/format";
import {
  buildOpportunityModels, offerCandidates, SEGMENT_CATEGORIES, segmentMembers,
  summarizeOpportunityModels,
} from "@/lib/segmentation";
import { useI18n } from "@/i18n";

const ACTIVE_OPPORTUNITY_STAGES = new Set(["New", "Qualifying", "Qualified", "Offered"]);
const FILTER_CATEGORIES = ["All", ...SEGMENT_CATEGORIES];

function relativeTime(value, t, formatNumber) {
  if (!value) return t("segments.summary.notSynced");
  const elapsed = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(elapsed / 60000));
  if (minutes < 1) return t("operations.time.justNow");
  if (minutes < 60) return t("operations.time.minutesAgo", { count: formatNumber(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("operations.time.hoursAgo", { count: formatNumber(hours) });
  return t("operations.time.daysAgo", { count: formatNumber(Math.floor(hours / 24)) });
}

function memberValue(members) {
  return members
    .filter((customer) => ACTIVE_OPPORTUNITY_STAGES.has(customer.stage))
    .reduce((sum, customer) => sum + Number(customer.value || 0), 0);
}

function formatRate(value, formatNumber) {
  return `${formatNumber(Math.round(Number(value || 0) * 100))}%`;
}

export default function SegmentsWorkspace({ onViewCustomer }) {
  const { state, actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewedId, setPreviewedId] = useState(null);
  const [workspaceView, setWorkspaceView] = useState("opportunities");
  const [calculationOpen, setCalculationOpen] = useState(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(null);

  const segments = useMemo(() => state.segments || [], [state.segments]);
  const selected = segments.find((segment) => segment.id === selectedId) || null;
  const sync = state.segmentSync || {};
  const contactable = state.customers.filter((customer) => customer.consent?.marketing).length;
  const openPotential = state.customers
    .filter((customer) => ACTIVE_OPPORTUNITY_STAGES.has(customer.stage))
    .reduce((sum, customer) => sum + Number(customer.value || 0), 0);

  const opportunities = useMemo(() => buildOpportunityModels({
    segments,
    customers: state.customers,
    bookings: state.bookings,
    conversations: state.conversations,
    services: state.services,
  }), [segments, state.bookings, state.conversations, state.customers, state.services]);
  const opportunitySummary = useMemo(() => summarizeOpportunityModels(opportunities), [opportunities]);
  const observed = opportunities[0] || {};
  const selectedOpportunity = opportunities.find((model) => model.id === selectedOpportunityId) || null;

  const openSegment = (segmentId, opportunityId = null) => {
    setSelectedOpportunityId(opportunityId);
    setSelectedId(segmentId);
  };

  const visibleSegments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return segments.filter((segment) => {
      const matchesCategory = category === "All" || segment.category === category;
      const matchesSearch = !normalizedQuery
        || `${segment.name} ${localize(segment.name)} ${segment.description} ${localize(segment.description)} ${segment.category} ${localize(segment.category)}`.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [category, localize, query, segments]);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 px-4 py-5 lg:px-8 lg:py-7">
      <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="flex flex-col gap-4 border-b border-border bg-primary-soft/30 p-4 sm:flex-row sm:items-start sm:justify-between lg:p-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ilia-soft text-ilia-soft-foreground">
              <BrainCircuit className="h-5 w-5" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold tracking-tight text-foreground">{t("segments.hero.title")}</h2>
                <Chip tone="ilia">{t("segments.hero.iliaAssisted")}</Chip>
              </div>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {t("segments.hero.description")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> {t("segments.actions.new")}
            </Button>
            <Button className="whitespace-nowrap" onClick={actions.refreshSegments}>
              <RefreshCcw className="mr-1.5 h-4 w-4" /> {t("segments.actions.refresh")}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-5">
          <div className="inline-flex w-full rounded-lg bg-muted p-1 sm:w-auto" aria-label={t("segments.views.aria")}>
            <button
              type="button"
              onClick={() => setWorkspaceView("opportunities")}
              className={`flex min-h-9 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors sm:flex-none ${workspaceView === "opportunities" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Target className="h-4 w-4" /> {t("segments.views.opportunities")}
            </button>
            <button
              type="button"
              onClick={() => setWorkspaceView("segments")}
              className={`flex min-h-9 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors sm:flex-none ${workspaceView === "segments" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Layers3 className="h-4 w-4" /> {t("segments.views.segments")}
            </button>
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            {workspaceView === "opportunities" ? t("segments.opportunity.updated") : t("segments.summary.analysisDetail", { chats: formatNumber(sync.conversationsChecked || 0), reviews: formatNumber(sync.pendingReview || 0) })}
          </p>
        </div>

        <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x lg:grid-cols-4 lg:divide-y-0">
          {workspaceView === "opportunities" ? (
            <>
              <SummaryMetric label={t("segments.opportunity.addressable")} value={money(opportunitySummary.addressableValue)} detail={t("segments.opportunity.addressableDetail")} />
              <SummaryMetric label={t("segments.opportunity.expected")} value={money(opportunitySummary.expectedValue)} detail={t("segments.opportunity.expectedDetail")} />
              <SummaryMetric label={t("segments.opportunity.readyPeople")} value={formatNumber(opportunitySummary.people)} detail={t("segments.opportunity.readyPeopleDetail")} />
              <SummaryMetric
                label={t("segments.opportunity.observedRates")}
                value={t("segments.opportunity.ratesValue", { booking: formatRate(observed.bookingRate, formatNumber), show: formatRate(observed.showRate, formatNumber) })}
                detail={t("segments.opportunity.ratesDetail", { conversations: formatNumber(observed.conversationSample || 0), visits: formatNumber(observed.attendanceSample || 0) })}
              />
            </>
          ) : (
            <>
              <SummaryMetric
                label={t("segments.summary.crmRecords")}
                value={formatNumber(state.customers.length)}
                detail={t("segments.summary.crmDetail", { system: localize(sync.crm || state.erp?.name) || "CRM", time: sync.lastPullAt ? relativeTime(sync.lastPullAt, t, formatNumber) : t("segments.summary.notSynced") })}
              />
              <SummaryMetric label={t("segments.summary.reachable")} value={formatNumber(contactable)} detail={t("segments.summary.consentOnRecord")} />
              <SummaryMetric label={t("segments.summary.openPotential")} value={money(openPotential)} detail={t("segments.summary.uniqueOpportunities")} />
              <SummaryMetric
                label={t("segments.summary.lastAnalysis")}
                value={sync.lastAnalysisAt ? relativeTime(sync.lastAnalysisAt, t, formatNumber) : t("segments.summary.notRun")}
                detail={t("segments.summary.analysisDetail", { chats: formatNumber(sync.conversationsChecked || 0), reviews: formatNumber(sync.pendingReview || 0) })}
              />
            </>
          )}
        </div>
      </section>

      {workspaceView === "opportunities" ? (
        <OpportunityMap
          models={opportunities}
          summary={opportunitySummary}
          onReview={(model) => openSegment(model.segment.id, model.id)}
          onCalculation={() => setCalculationOpen(true)}
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("segments.searchPlaceholder")}
                aria-label={t("segments.searchAria")}
                className="bg-surface pl-9"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full bg-surface sm:w-[200px]" aria-label={t("segments.categoryFilterAria")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_CATEGORIES.map((item) => <SelectItem key={item} value={item}>{item === "All" ? t("segments.allCategories") : localize(item)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">{t("segments.title")}</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("segments.tableDescription")}</p>
              </div>
              <Chip tone="muted">{t("segments.count", { count: formatNumber(visibleSegments.length) })}</Chip>
            </div>

            {visibleSegments.length ? (
              <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">{t("segments.columns.segment")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("segments.columns.members")}</TableHead>
                <TableHead className="hidden lg:table-cell">{t("segments.columns.reachable")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("segments.columns.openPotential")}</TableHead>
                <TableHead className="w-10"><span className="sr-only">{t("segments.actions.open")}</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleSegments.map((segment) => {
                const members = segmentMembers(segment, state.customers);
                const reachable = members.filter((customer) => customer.consent?.marketing).length;
                return (
                  <TableRow key={segment.id}>
                    <TableCell className="pl-4 py-3">
                      <button className="group w-full text-left" onClick={() => openSegment(segment.id)}>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-foreground group-hover:text-primary">{localize(segment.name)}</span>
                          <SourceTag source={segment.source} />
                        </span>
                        <span className="mt-1 block max-w-xl text-xs leading-relaxed text-muted-foreground">{localize(segment.description)}</span>
                        <span className="mt-2 flex items-center gap-2 md:hidden">
                          <Chip tone="muted">{t("segments.peopleCount", { count: formatNumber(members.length) })}</Chip>
                          <span className="text-xs font-medium text-foreground">{t("segments.openValue", { value: money(memberValue(members)) })}</span>
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="font-medium text-foreground">{formatNumber(members.length)}</p>
                      <p className="text-xs text-muted-foreground">{localize(segment.category)}</p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <p className="font-medium text-foreground">{formatNumber(reachable)}</p>
                      <p className="text-xs text-muted-foreground">{t("segments.withConsent")}</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell font-semibold text-foreground">{money(memberValue(members))}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" aria-label={t("segments.openAria", { name: localize(segment.name) })} onClick={() => openSegment(segment.id)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
              </Table>
            ) : (
              <div className="px-6 py-12 text-center">
                <Layers3 className="mx-auto h-6 w-6 text-muted-foreground" />
                <p className="mt-3 text-sm font-semibold text-foreground">{t("segments.empty.title")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t("segments.empty.description")}</p>
              </div>
            )}
          </section>
        </>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>{t("segments.compliance")}</p>
      </div>

      <CreateSegmentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => openSegment(id)} />
      <OpportunityCalculationDialog
        open={calculationOpen}
        onOpenChange={setCalculationOpen}
        summary={opportunitySummary}
        observed={observed}
      />
      <SegmentDetailSheet
        segment={selected}
        opportunity={selectedOpportunity}
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedId(null);
            setSelectedOpportunityId(null);
          }
        }}
        onViewCustomer={onViewCustomer}
        previewed={previewedId === selected?.id}
        onPreviewed={() => setPreviewedId(selected?.id || null)}
      />
    </div>
  );
}

function OpportunityMap({ models, summary, onReview, onCalculation }) {
  const { t, formatNumber } = useI18n();
  const observed = models[0] || {};

  if (!models.length) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface px-6 py-14 text-center shadow-xs">
        <Target className="mx-auto h-7 w-7 text-muted-foreground" />
        <h2 className="mt-3 text-sm font-semibold text-foreground">{t("segments.opportunity.emptyTitle")}</h2>
        <p className="mx-auto mt-1 max-w-lg text-sm leading-relaxed text-muted-foreground">{t("segments.opportunity.emptyDescription")}</p>
      </section>
    );
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
      <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="border-b border-border px-4 py-4 lg:px-5">
          <h2 className="text-sm font-semibold text-foreground">{t("segments.opportunity.priorityTitle")}</h2>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("segments.opportunity.priorityDescription")}</p>
        </div>
        <div className="divide-y divide-border">
          {models.map((model, index) => (
            <OpportunityRow key={model.id} model={model} rank={index + 1} onReview={onReview} />
          ))}
        </div>
      </section>

      <aside className="space-y-4 lg:sticky lg:top-5">
        <section className="rounded-xl border border-border bg-surface p-4 shadow-xs lg:p-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <BarChart03 className="h-[18px] w-[18px]" />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-foreground">{t("segments.opportunity.calculationTitle")}</h2>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("segments.opportunity.calculationDescription")}</p>
            </div>
          </div>
          <dl className="mt-4 divide-y divide-border border-y border-border">
            <CalculationLine label={t("segments.opportunity.bookingRate")} value={formatRate(observed.bookingRate, formatNumber)} detail={t("segments.opportunity.sampleConversations", { count: formatNumber(observed.conversationSample || 0) })} />
            <CalculationLine label={t("segments.opportunity.showRate")} value={formatRate(observed.showRate, formatNumber)} detail={t("segments.opportunity.sampleVisits", { count: formatNumber(observed.attendanceSample || 0) })} />
            <CalculationLine label={t("segments.opportunity.overlapsRemoved")} value={formatNumber(summary.overlapsRemoved)} detail={t("segments.opportunity.highestValueKept")} />
          </dl>
          <Button variant="outline" className="mt-4 w-full" onClick={onCalculation}>
            <Info className="mr-1.5 h-4 w-4" /> {t("segments.opportunity.howCalculated")}
          </Button>
        </section>

        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3.5 py-3 text-xs leading-5 text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>{t("segments.opportunity.notRevenue")}</p>
        </div>
      </aside>
    </div>
  );
}

function OpportunityRow({ model, rank, onReview }) {
  const { t, localize, formatNumber } = useI18n();
  const booking = formatRate(model.bookingRate, formatNumber);
  const show = formatRate(model.showRate, formatNumber);
  const statusKey = model.contactable.length
    ? model.excluded.length ? "needsConsent" : "ready"
    : "noAudience";
  const equation = model.kind === "cross_sell"
    ? t("segments.opportunity.equationCrossSell", { people: formatNumber(model.contactable.length), price: money(model.nextService?.price || 0) })
    : t("segments.opportunity.equationOpen", { people: formatNumber(model.contactable.length) });

  return (
    <article className="p-4 transition-colors hover:bg-muted/20 lg:p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={rank === 1 ? "success" : "muted"}>{t("segments.opportunity.rank", { rank: formatNumber(rank) })}</Chip>
            <Chip tone={statusKey === "ready" ? "success" : statusKey === "needsConsent" ? "warning" : "muted"}>{t(`segments.opportunity.${statusKey}`)}</Chip>
          </div>
          <h3 className="mt-2.5 text-sm font-semibold text-foreground">{t(`segments.opportunity.${model.id}.title`)}</h3>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-muted-foreground">{t(`segments.opportunity.${model.id}.description`)}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>{t("segments.opportunity.members", { count: formatNumber(model.members.length) })}</span>
            <span>{t("segments.opportunity.eligible", { count: formatNumber(model.eligible.length) })}</span>
            <span className="font-medium text-foreground">{t("segments.opportunity.contactable", { count: formatNumber(model.contactable.length) })}</span>
            <span>{localize(model.segment.name)}</span>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border xl:w-[310px]">
          <div className="bg-surface p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <CircleDollarSign className="h-3.5 w-3.5" />
              <p className="text-[11px] font-medium">{t("segments.opportunity.addressableValue")}</p>
            </div>
            <p className="mt-1 text-lg font-semibold tracking-tight text-foreground">{money(model.addressableValue)}</p>
            <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{equation}</p>
          </div>
          <div className="bg-surface p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" />
              <p className="text-[11px] font-medium">{t("segments.opportunity.expectedValue")}</p>
            </div>
            <p className="mt-1 text-lg font-semibold tracking-tight text-foreground">{money(model.expectedValue)}</p>
            <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{t("segments.opportunity.ratePair", { booking, show })}</p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-5 text-muted-foreground">{t("segments.opportunity.modelledWith", { booking, show })}</p>
        <Button size="sm" variant="outline" onClick={() => onReview(model)}>
          <UserCheck className="mr-1.5 h-4 w-4" /> {t("segments.opportunity.reviewAudience")}
        </Button>
      </div>
    </article>
  );
}

function CalculationLine({ label, value, detail }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div>
        <dt className="text-xs font-medium text-foreground">{label}</dt>
        <dd className="mt-0.5 text-[11px] text-muted-foreground">{detail}</dd>
      </div>
      <dd className="text-sm font-semibold text-foreground">{value}</dd>
    </div>
  );
}

function OpportunityCalculationDialog({ open, onOpenChange, summary, observed }) {
  const { t, formatNumber } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("segments.opportunity.howCalculated")}</DialogTitle>
          <DialogDescription>{t("segments.opportunity.calculationDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <FormulaBlock icon={CircleDollarSign} title={t("segments.opportunity.addressableFormula")} description={t("segments.opportunity.addressableFormulaDetail")} />
          <FormulaBlock icon={TrendingUp} title={t("segments.opportunity.expectedFormula")} description={t("segments.opportunity.expectedFormulaDetail", { booking: formatRate(observed.bookingRate, formatNumber), show: formatRate(observed.showRate, formatNumber) })} />
          <FormulaBlock icon={UsersRoundSafe} title={t("segments.opportunity.noDoubleCount")} description={t("segments.opportunity.noDoubleCountDetail", { count: formatNumber(summary.overlapsRemoved) })} />
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t("segments.opportunity.understood")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersRoundSafe(props) {
  return <UserCheck {...props} />;
}

function FormulaBlock({ icon: Icon, title, description }) {
  return (
    <section className="flex items-start gap-3 rounded-lg border border-border bg-muted/25 p-3.5">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface text-primary shadow-xs">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
      </div>
    </section>
  );
}

function SummaryMetric({ label, value, detail }) {
  return (
    <div className="min-w-0 p-4 lg:min-h-[124px]">
      <p className="min-h-8 text-xs font-medium leading-4 text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-xl font-semibold tracking-tight text-foreground">{value}</p>
      <p className="mt-1 text-xs leading-4 text-muted-foreground">{detail}</p>
    </div>
  );
}

function SegmentDetailSheet({ segment, opportunity, open, onOpenChange, onViewCustomer, previewed, onPreviewed }) {
  const { state } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const [membershipOpen, setMembershipOpen] = useState(false);
  if (!segment) return null;

  const members = segmentMembers(segment, state.customers);
  const reachable = members.filter((customer) => customer.consent?.marketing).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full !w-full flex-col gap-0 overflow-hidden p-0 sm:!max-w-xl">
        <SheetHeader className="border-b border-border p-5 pr-12 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle>{localize(segment.name)}</SheetTitle>
            <SourceTag source={segment.source} />
          </div>
          <SheetDescription>{localize(segment.description)}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto p-5 scroll-thin">
          <div className="grid grid-cols-3 divide-x divide-border rounded-lg border border-border bg-surface">
            <CompactMetric label={t("segments.detail.members")} value={formatNumber(members.length)} />
            <CompactMetric label={t("segments.detail.reachable")} value={formatNumber(reachable)} />
            <CompactMetric label={t("segments.detail.openPotential")} value={money(memberValue(members))} />
          </div>

          {opportunity ? <OpportunityAudienceAudit opportunity={opportunity} onViewCustomer={onViewCustomer} /> : null}

          <section>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t("segments.detail.whyMatch")}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("segments.detail.evidenceDescription")}</p>
              </div>
              {segment.membershipLocked ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toast(t("segments.sourceToast.title"), {
                    description: segment.source === "imported"
                      ? t("segments.sourceToast.importedDescription")
                      : t("segments.sourceToast.opportunityDescription"),
                  })}
                >
                  {t("segments.actions.correctSource")}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setMembershipOpen(true)}>{t("segments.actions.editMembership")}</Button>
              )}
            </div>

            <div className="mt-3 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {members.length ? members.map((customer) => (
                <button
                  key={customer.id}
                  className="flex w-full items-start gap-3 bg-surface p-3 text-left transition-colors hover:bg-muted/50"
                  onClick={() => onViewCustomer(customer.id)}
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                    {initials(localize(customer.name))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{localize(customer.name)}</span>
                      {customer.consent?.marketing ? <Chip tone="success">{t("segments.contactable")}</Chip> : <Chip tone="muted">{t("segments.noMarketingConsent")}</Chip>}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {localize(segment.memberEvidence?.[customer.id]) || t("segments.detail.addedByTeam")}
                    </span>
                    <span className="mt-1.5 flex flex-wrap items-center gap-2">
                      <StageBadge stage={customer.stage} />
                      <span className="text-xs font-semibold text-foreground">{t("segments.potentialValue", { value: money(customer.value) })}</span>
                    </span>
                  </span>
                  <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              )) : (
                <div className="p-6 text-center text-sm text-muted-foreground">{t("segments.detail.noMatches")}</div>
              )}
            </div>
          </section>

          {!opportunity ? <OfferReadiness segment={segment} previewed={previewed} onPreviewed={onPreviewed} /> : null}

          <section className="rounded-lg border border-border bg-muted/35 p-3.5">
            <div className="flex items-start gap-2.5">
              <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">{t("segments.provenance.title")}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {t("segments.provenance.description", {
                    time: segment.lastRefreshed ? relativeTime(segment.lastRefreshed, t, formatNumber) : t("segments.provenance.never"),
                  })}
                </p>
              </div>
            </div>
          </section>
        </div>

        <SegmentMembershipDialog segment={segment} open={membershipOpen} onOpenChange={setMembershipOpen} />
      </SheetContent>
    </Sheet>
  );
}

function OpportunityAudienceAudit({ opportunity, onViewCustomer }) {
  const { t, localize, formatNumber } = useI18n();
  return (
    <section className="overflow-hidden rounded-lg border border-primary/25 bg-primary-soft/20">
      <div className="border-b border-primary/15 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("segments.opportunity.audienceTitle")}</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("segments.opportunity.audienceDescription")}</p>
          </div>
          {opportunity.excluded.length ? <Chip tone="warning">{t("segments.opportunity.excludedCount", { count: formatNumber(opportunity.excluded.length) })}</Chip> : <Chip tone="success">{t("segments.opportunity.allContactable")}</Chip>}
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-primary/15 border-b border-primary/15">
        <CompactMetric label={t("segments.opportunity.eligibleLabel")} value={formatNumber(opportunity.eligible.length)} />
        <CompactMetric label={t("segments.opportunity.contactableLabel")} value={formatNumber(opportunity.contactable.length)} />
        <CompactMetric label={t("segments.opportunity.expectedValue")} value={money(opportunity.expectedValue)} />
      </div>
      {opportunity.contactable.length ? (
        <div className="max-h-64 divide-y divide-border overflow-y-auto bg-surface scroll-thin">
          {opportunity.contactable.map((customer) => (
            <button
              key={customer.id}
              type="button"
              className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-muted/50"
              onClick={() => onViewCustomer(customer.id)}
            >
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
                {initials(localize(customer.name))}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">{localize(customer.name)}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {opportunity.nextService ? localize(opportunity.nextService.name) : t("segments.opportunity.activeDeal")}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold text-foreground">{money(opportunity.valueFor(customer))}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      ) : (
        <p className="bg-surface px-4 py-5 text-center text-sm text-muted-foreground">{t("segments.opportunity.noContactable")}</p>
      )}
    </section>
  );
}

function CompactMetric({ label, value }) {
  return (
    <div className="min-w-0 p-3">
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-base font-semibold text-foreground">{value}</p>
    </div>
  );
}

function OfferReadiness({ segment, previewed, onPreviewed }) {
  const { state } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const eligible = offerCandidates(segment, state);
  const contactable = eligible.filter((customer) => customer.consent?.marketing);
  const excludedForConsent = eligible.filter((customer) => !customer.consent?.marketing);
  const identityReviews = segmentMembers(segment, state.customers).filter((customer) => customer.mergePending);
  const nextService = state.services.find((service) => service.id === segment.offer?.nextServiceId);
  const grossOfferValue = contactable.length * Number(nextService?.price || 0);

  if (!segment.offer) {
    return (
      <section className="rounded-lg border border-dashed border-border p-4">
        <h3 className="text-sm font-semibold text-foreground">{t("segments.offer.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("segments.offer.notConfigured")}</p>
        <Button className="mt-3" size="sm" variant="outline" onClick={() => toast(t("segments.offer.futureToast"), { description: t("segments.offer.nothingCreated") })}>
          {t("segments.offer.reviewPossibilities")}
        </Button>
      </section>
    );
  }

  const preview = () => {
    if (!contactable.length) return;
    onPreviewed();
    toast.success(t("segments.offer.previewReady"), {
      description: t("segments.offer.previewReadyDescription", { count: formatNumber(contactable.length) }),
    });
  };

  return (
    <section className="overflow-hidden rounded-lg border border-primary/25 bg-primary-soft/25">
      <div className="flex items-start gap-3 border-b border-primary/15 p-4">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t("segments.offer.suggestedService")}</h3>
          <p className="mt-0.5 text-sm text-foreground">{localize(segment.offer.title)}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t("segments.offer.basis", {
              service: localize(nextService?.name) || t("segments.offer.nextServiceFallback"),
              days: formatNumber(segment.offer.exclusionWindowDays || 90),
            })}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-primary/15">
        <CompactMetric label={t("segments.offer.eligible")} value={formatNumber(eligible.length)} />
        <CompactMetric label={t("segments.offer.withConsent")} value={formatNumber(contactable.length)} />
        <CompactMetric label={t("segments.offer.grossValue")} value={money(grossOfferValue)} />
      </div>
      <div className="flex flex-col gap-3 border-t border-primary/15 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-muted-foreground">
          {!contactable.length
            ? identityReviews.length
              ? t("segments.offer.noConsentWithReviews", { count: formatNumber(identityReviews.length) })
              : t("segments.offer.noConsent")
            : previewed
              ? t("segments.offer.previewVisible")
              : t("segments.offer.previewPrompt")}
        </p>
        <Button size="sm" disabled={!contactable.length || previewed} onClick={preview} className="shrink-0">
          {previewed ? t("segments.offer.previewOpen") : t("segments.offer.previewAudience")}
          {!previewed && <ArrowRight className="ml-1.5 h-4 w-4" />}
        </Button>
      </div>
      {previewed ? (
        <div className="border-t border-primary/15 bg-surface p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">{t("segments.offer.audiencePreview")}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("segments.offer.reviewPeople")}</p>
            </div>
            <Chip tone="success">{t("segments.peopleCount", { count: formatNumber(contactable.length) })}</Chip>
          </div>
          <div className="mt-3 divide-y divide-border overflow-hidden rounded-lg border border-border">
            {contactable.map((customer) => (
              <div key={customer.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{localize(customer.name)}</p>
                  <p className="text-xs text-muted-foreground">{t("segments.offer.marketingConsent")}</p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-foreground">{money(nextService?.price || 0)}</span>
              </div>
            ))}
          </div>
          {(excludedForConsent.length > 0 || identityReviews.length > 0) && (
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {excludedForConsent.length > 0 ? t("segments.offer.excludedConsent", { count: formatNumber(excludedForConsent.length) }) : ""}
              {excludedForConsent.length > 0 && identityReviews.length > 0 ? " " : ""}
              {identityReviews.length > 0 ? t("segments.offer.excludedIdentity", { count: formatNumber(identityReviews.length) }) : ""}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function CreateSegmentDialog({ open, onOpenChange, onCreated }) {
  const { state, actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Behavior");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState([]);

  const reset = () => {
    setName("");
    setCategory("Behavior");
    setDescription("");
    setMemberIds([]);
  };

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const toggle = (customerId, checked) => {
    setMemberIds((current) => checked
      ? Array.from(new Set([...current, customerId]))
      : current.filter((id) => id !== customerId));
  };

  const selectedValue = state.customers
    .filter((customer) => memberIds.includes(customer.id) && ACTIVE_OPPORTUNITY_STAGES.has(customer.stage))
    .reduce((sum, customer) => sum + Number(customer.value || 0), 0);

  const create = () => {
    const id = actions.addSegment({ name: name.trim(), category, description: description.trim(), memberIds });
    handleOpenChange(false);
    onCreated(id);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("segments.create.title")}</DialogTitle>
          <DialogDescription>{t("segments.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <label htmlFor="segment-name" className="text-sm font-medium text-foreground">{t("segments.create.name")}</label>
            <Input id="segment-name" value={name} onChange={(event) => setName(event.target.value)} placeholder={t("segments.create.namePlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="segment-category" className="text-sm font-medium text-foreground">{t("segments.create.category")}</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="segment-category"><SelectValue /></SelectTrigger>
              <SelectContent>{SEGMENT_CATEGORIES.map((item) => <SelectItem key={item} value={item}>{localize(item)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="segment-description" className="text-sm font-medium text-foreground">{t("segments.create.purpose")}</label>
            <Textarea id="segment-description" value={description} onChange={(event) => setDescription(event.target.value)} rows={2} placeholder={t("segments.create.purposePlaceholder")} className="resize-none" />
          </div>

          <div>
            <div className="mb-2 flex items-end justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t("segments.create.people")}</p>
                <p className="text-xs text-muted-foreground">{t("segments.create.selectAudience")}</p>
              </div>
              <p className="text-xs font-medium text-foreground">{t("segments.create.selectionSummary", { count: formatNumber(memberIds.length), value: money(selectedValue) })}</p>
            </div>
            <div className="max-h-64 divide-y divide-border overflow-y-auto rounded-lg border border-border scroll-thin">
              {state.customers.map((customer) => {
                const checked = memberIds.includes(customer.id);
                return (
                  <label key={customer.id} className="flex cursor-pointer items-center gap-3 p-3 hover:bg-muted/50">
                    <Checkbox checked={checked} onCheckedChange={(value) => toggle(customer.id, Boolean(value))} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{localize(customer.name)}</span>
                      <span className="block truncate text-xs text-muted-foreground">{localize(customer.qualification[0]?.value) || t("segments.create.interestNotSet")}</span>
                    </span>
                    <span className="text-xs font-semibold text-foreground">{money(customer.value)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t("segments.actions.cancel")}</Button>
          <Button disabled={!name.trim() || !description.trim()} onClick={create}>{t("segments.actions.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SegmentMembershipDialog({ segment, open, onOpenChange }) {
  const { state, actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const [memberIds, setMemberIds] = useState(segment.memberIds || []);
  if (segment.membershipLocked) return null;

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) setMemberIds(segment.memberIds || []);
    onOpenChange(nextOpen);
  };

  const toggle = (customerId, checked) => {
    setMemberIds((current) => checked
      ? Array.from(new Set([...current, customerId]))
      : current.filter((id) => id !== customerId));
  };

  const save = () => {
    actions.setSegmentMembers(segment.id, memberIds);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("segments.membership.title", { name: localize(segment.name) })}</DialogTitle>
          <DialogDescription>{t("segments.membership.description")}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[52vh] divide-y divide-border overflow-y-auto rounded-lg border border-border scroll-thin">
          {state.customers.map((customer) => {
            const checked = memberIds.includes(customer.id);
            return (
              <label key={customer.id} className="flex cursor-pointer items-center gap-3 p-3 hover:bg-muted/50">
                <Checkbox checked={checked} onCheckedChange={(value) => toggle(customer.id, Boolean(value))} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{localize(customer.name)}</span>
                  <span className="block truncate text-xs text-muted-foreground">{localize(segment.memberEvidence?.[customer.id]) || t("segments.membership.noEvidence")}</span>
                </span>
                <span className="text-xs font-semibold text-foreground">{money(customer.value)}</span>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("segments.actions.cancel")}</Button>
          <Button onClick={save}>{t("segments.membership.save", { count: formatNumber(memberIds.length) })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
